/**
 * Product Selector Bundle Handler
 * Handles adding multiple products to cart when premium bundle is selected
 */

class ProductSelectorBundle {
  constructor() {
    // Singleton pattern - prevent multiple instances
    if (ProductSelectorBundle.instance) {
      return ProductSelectorBundle.instance;
    }
    ProductSelectorBundle.instance = this;
    this.init();
  }

  init() {
    const productSelector = document.querySelector('[data-product-selector]');
    if (!productSelector) {
      return;
    }

    const options = productSelector.querySelectorAll('.product-option');
    
    if (options.length === 0) {
      return;
    }
    
    this.productSelector = productSelector;
    this.options = options;
    
    // Check if we're on the standalone premium product page
    const isStandalonePremiumPage = window.location.pathname.includes('gpath-pin-1-year-premium-1');
    
    // Initialize selected state based on current page
    if (isStandalonePremiumPage) {
      this.selectedOptionType = 'standalone-premium';
      sessionStorage.setItem('selectedProductOption', 'standalone-premium');
      // Find and select the standalone premium option
      const standaloneOption = Array.from(options).find(opt => 
        opt.dataset.optionType === 'standalone-premium'
      );
      if (standaloneOption) {
        options.forEach(opt => opt.classList.remove('selected'));
        standaloneOption.classList.add('selected');
      }
    } else {
      this.selectedOptionType = 'basic';
      sessionStorage.setItem('selectedProductOption', 'basic');
    }
    
    this.setupFormInterceptor();
    this.setupOptionHandlers();
    this.setupCurrencyListener();
    
    // Update price on initial load (run immediately and also after delay)
    const updatePrices = () => {
      const selectedOption = document.querySelector('.product-option.selected');
      if (selectedOption) {
        this.updateMainPrice(selectedOption);
      }
      
      // Update option prices on initial load
      this.updateOptionPrices();
    };
    
    // Run immediately
    updatePrices();
    
    // Also run after a delay to catch any late currency conversions
    setTimeout(updatePrices, 100);
  }
  
  setupCurrencyListener() {
    // Listen for currency changes
    // Shopify themes typically trigger custom events or update the DOM
    // We'll use a MutationObserver to watch for price changes
    const observer = new MutationObserver(() => {
      this.updateOptionPrices();
    });
    
    // Observe the product info section for price changes
    const productInfo = document.querySelector('product-info');
    if (productInfo) {
      observer.observe(productInfo, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
    
    // Also listen for custom currency change events
    document.addEventListener('currency:change', () => {
      this.updateOptionPrices();
    });
    
    // Listen for Shopify currency changes (if available)
    if (window.Shopify && window.Shopify.currency) {
      const originalSetCurrency = window.Shopify.currency.setCurrency;
      if (originalSetCurrency) {
        window.Shopify.currency.setCurrency = (...args) => {
          const result = originalSetCurrency.apply(window.Shopify.currency, args);
          setTimeout(() => this.updateOptionPrices(), 100);
          return result;
        };
      }
    }
  }
  
  getCurrencyConversionRate() {
    // Try to get the conversion rate by comparing original price to displayed price
    // First, try to get it from the main product price display
    const productInfo = document.querySelector('product-info');
    if (productInfo) {
      const sectionId = productInfo.dataset.section;
      const priceElement = document.querySelector(`#price-${sectionId}`);
      if (priceElement) {
        // Get the current displayed price (already converted by Shopify)
        const currentPriceText = priceElement.querySelector('.price-item--sale, .price-item--regular, .price__regular .price-item')?.textContent;
        if (currentPriceText) {
          // Get the original price from the first option (basic)
          const basicOption = this.options[0];
          if (basicOption) {
            const originalPriceCents = parseInt(basicOption.dataset.originalPrice);
            if (originalPriceCents && originalPriceCents > 0) {
              // Parse the displayed price
              const displayedPrice = this.parsePrice(currentPriceText);
              if (displayedPrice > 0) {
                // Calculate conversion rate
                const originalPrice = originalPriceCents / 100;
                const rate = displayedPrice / originalPrice;
                if (rate > 0 && rate < 10) { // Sanity check: rate should be reasonable
                  return rate;
                }
              }
            }
          }
        }
      }
    }
    
    // Fallback: try to get rate from option card prices (if they were rendered by Liquid with currency conversion)
    const basicOption = this.options[0];
    if (basicOption) {
      const originalPriceCents = parseInt(basicOption.dataset.originalPrice);
      const displayedPriceText = basicOption.querySelector('.product-option__compare-price')?.textContent;
      if (originalPriceCents && originalPriceCents > 0 && displayedPriceText) {
        const displayedPrice = this.parsePrice(displayedPriceText);
        if (displayedPrice > 0) {
          const originalPrice = originalPriceCents / 100;
          const rate = displayedPrice / originalPrice;
          if (rate > 0 && rate < 10) {
            return rate;
          }
        }
      }
    }
    
    // Default: no conversion (same currency)
    return 1;
  }
  
  parsePrice(priceText) {
    // Remove currency symbols and parse the number
    const cleaned = priceText.replace(/[^\d.,]/g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  }
  
  updateOptionPrices() {
    const conversionRate = this.getCurrencyConversionRate();
    
    this.options.forEach((option) => {
      const originalPriceCents = parseInt(option.dataset.originalPrice);
      const discountAmountCents = parseInt(option.dataset.discountAmount) || 0;
      const comparePriceCents = parseInt(option.dataset.comparePrice);
      const currentPriceCents = parseInt(option.dataset.price);
      
      let discountedPrice;
      let convertedComparePrice;
      
      // Handle options with discount amount (basic and standalone-premium)
      if (originalPriceCents && originalPriceCents > 0 && discountAmountCents > 0) {
        // Calculate discounted price in current currency
        const originalPrice = originalPriceCents / 100;
        const discountAmount = discountAmountCents / 100;
        discountedPrice = Math.max(0, (originalPrice - discountAmount) * conversionRate);
        
        // Use compare price (the "before" price) for display and percentage calculation
        const comparePrice = (comparePriceCents || originalPriceCents) / 100;
        convertedComparePrice = comparePrice * conversionRate;
      } 
      // Handle premium bundle option (no discount amount, uses combined price)
      else if (currentPriceCents && currentPriceCents > 0) {
        // For premium bundle, use the existing prices and convert them
        discountedPrice = (currentPriceCents / 100) * conversionRate;
        convertedComparePrice = (comparePriceCents || currentPriceCents) / 100 * conversionRate;
      } else {
        return; // Skip if we don't have enough data
      }
      
      // Update the displayed prices in the option card
      const priceElement = option.querySelector('.product-option__price');
      const comparePriceElement = option.querySelector('.product-option__compare-price');
      const discountElement = option.querySelector('.product-option__discount');
      
      if (priceElement) {
        priceElement.textContent = this.formatMoney(discountedPrice * 100);
      }
      
      if (comparePriceElement) {
        comparePriceElement.textContent = this.formatMoney(convertedComparePrice * 100);
      }
      
      // Update discount percentage based on compare price (the "before" price)
      if (discountElement && convertedComparePrice > 0 && convertedComparePrice > discountedPrice) {
        const discountPercent = Math.round(((convertedComparePrice - discountedPrice) / convertedComparePrice) * 100);
        discountElement.textContent = `${discountPercent}% OFF`;
      } else if (discountElement) {
        discountElement.textContent = '0% OFF';
      }
      
      // Update data attributes for main price update
      option.dataset.price = Math.round(discountedPrice * 100);
      option.dataset.comparePrice = Math.round(convertedComparePrice * 100);
    });
    
    // Update main price if an option is selected
    const selectedOption = document.querySelector('.product-option.selected');
    if (selectedOption) {
      this.updateMainPrice(selectedOption);
    }
  }

  setupOptionHandlers() {
    this.options.forEach((option) => {
      const optionType = option.dataset.optionType || option.getAttribute('data-option-type');
      
      // Use a simple click handler
      option.addEventListener('click', (e) => {
        if (!optionType) {
          return;
        }
        
        // If standalone-premium is selected, navigate to that product page
        if (optionType === 'standalone-premium') {
          window.location.href = 'https://getgpath.com/products/gpath-pin-1-year-premium-1';
          return;
        }
        
        // Update selected state
        this.options.forEach(opt => opt.classList.remove('selected'));
        option.classList.add('selected');
        
        // Store selection
        this.selectedOptionType = optionType;
        
        // Update main price display
        try {
          this.updateMainPrice(option);
        } catch (err) {
          console.error('Error updating main price:', err);
        }
        
        // Store in sessionStorage for form submission
        sessionStorage.setItem('selectedProductOption', optionType);
        
        const premiumVariantId = option.getAttribute('data-premium-variant-id');
        const standaloneVariantId = option.getAttribute('data-variant-id');
        
        if (optionType === 'premium' && premiumVariantId) {
          sessionStorage.setItem('premiumVariantId', premiumVariantId);
          sessionStorage.removeItem('standaloneVariantId');
        } else if (optionType === 'standalone-premium' && standaloneVariantId) {
          sessionStorage.setItem('standaloneVariantId', standaloneVariantId);
          sessionStorage.removeItem('premiumVariantId');
        } else {
          sessionStorage.removeItem('premiumVariantId');
          sessionStorage.removeItem('standaloneVariantId');
        }
      });
    });
  }

  setupFormInterceptor() {
    const productFormElement = document.querySelector('product-form');
    const productForm = document.querySelector("form[data-type='add-to-cart-form']");
    
    if (!productForm) {
      setTimeout(() => this.setupFormInterceptor(), 100);
      return;
    }
    
    // Store reference to the original product-form submit handler
    // We'll use capture phase to intercept BEFORE the product-form element processes it
    productForm.addEventListener('submit', (e) => {
      const selectedOption = sessionStorage.getItem('selectedProductOption') || 'basic';
      
      if (selectedOption === 'premium') {
        // Prevent default form submission AND stop propagation to product-form
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        this.handlePremiumBundleSubmit(e, productForm, productFormElement);
      } else if (selectedOption === 'standalone-premium') {
        // Prevent default form submission AND stop propagation to product-form
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        this.handleStandalonePremiumSubmit(e, productForm, productFormElement);
      }
      // If basic option, let the form submit normally (product-form will handle it)
    }, true); // Use capture phase to ensure we run BEFORE product-form's handler
  }

  async handlePremiumBundleSubmit(e, productForm, productFormElement) {
    const formData = new FormData(productForm);
    const currentVariantIdStr = formData.get('id');
    const quantityStr = formData.get('quantity') || '1';
    
    // Validate and convert variant ID to number
    const currentVariantId = parseInt(currentVariantIdStr);
    const quantity = parseInt(quantityStr) || 1;
    
    // Validate variant ID
    if (isNaN(currentVariantId) || currentVariantId <= 0) {
      alert('Invalid product variant. Please refresh the page and try again.');
      return;
    }
    
    if (isNaN(quantity) || quantity <= 0) {
      alert('Invalid quantity. Please enter a valid quantity.');
      return;
    }
    
    // Get submit button and show loading state
    const submitButton = productForm.querySelector("[type='submit']");
    const loadingSpinner = submitButton?.parentElement?.querySelector('.loading__spinner');
    
    if (submitButton) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
      if (loadingSpinner) {
        loadingSpinner.classList.remove('hidden');
      }
    }
    
    try {
      await this.addProductToCart(currentVariantId, quantity);
    } catch (error) {
      console.error('Error adding product to cart:', error);
      alert(error.message || 'There was an error adding the product to your cart. Please try again.');
    } finally {
      // Re-enable button
      if (submitButton) {
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
        if (loadingSpinner) {
          loadingSpinner.classList.add('hidden');
        }
      }
    }
  }

  async handleStandalonePremiumSubmit(e, productForm, productFormElement) {
    const standaloneVariantIdStr = sessionStorage.getItem('standaloneVariantId');
    const formData = new FormData(productForm);
    const quantityStr = formData.get('quantity') || '1';
    
    // Validate and convert variant ID to number
    const standaloneVariantId = parseInt(standaloneVariantIdStr);
    const quantity = parseInt(quantityStr) || 1;
    
    // Validate variant ID
    if (isNaN(standaloneVariantId) || standaloneVariantId <= 0) {
      alert('Invalid product variant. Please refresh the page and try again.');
      return;
    }
    
    if (isNaN(quantity) || quantity <= 0) {
      alert('Invalid quantity. Please enter a valid quantity.');
      return;
    }
    
    // Get submit button and show loading state
    const submitButton = productForm.querySelector("[type='submit']");
    const loadingSpinner = submitButton?.parentElement?.querySelector('.loading__spinner');
    
    if (submitButton) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
      if (loadingSpinner) {
        loadingSpinner.classList.remove('hidden');
      }
    }
    
    try {
      await this.addProductToCart(standaloneVariantId, quantity);
    } catch (error) {
      console.error('Error adding product to cart:', error);
      alert(error.message || 'There was an error adding the product to your cart. Please try again.');
    } finally {
      // Re-enable button
      if (submitButton) {
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
        if (loadingSpinner) {
          loadingSpinner.classList.add('hidden');
        }
      }
    }
  }

  async addProductToCart(variantId, quantity) {
    // Use the Shopify Cart API to add the product
    const cartAddUrl = '/cart/add.js';
    
    // Get cart element to request sections
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    let sectionsToRender = [];
    if (cart && typeof cart.getSectionsToRender === 'function') {
      sectionsToRender = cart.getSectionsToRender().map((section) => section.id);
    }
    
    // Prepare request body with single item
    const requestBody = {
      items: [
        {
          id: variantId,
          quantity: quantity
        }
      ]
    };
    
    // Add sections if available (for cart drawer/notification)
    if (sectionsToRender.length > 0) {
      requestBody.sections = sectionsToRender.join(',');
      requestBody.sections_url = window.location.pathname;
    }
    
    // Add product to cart
    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(requestBody)
    });
    
    // Read response as text first to handle both JSON and HTML
    const responseText = await response.text();
    
    if (!response.ok) {
      // Try to parse as JSON to get error details
      try {
        const errorJson = JSON.parse(responseText);
        throw new Error('Failed to add product to cart: ' + (errorJson.description || errorJson.message || 'Unknown error'));
      } catch (e) {
        throw new Error('Failed to add product to cart. Server response: ' + responseText.substring(0, 100));
      }
    }
    
    // Try to parse as JSON regardless of content-type (some themes return wrong content-type)
    let result;
    try {
      result = JSON.parse(responseText);
    } catch (parseError) {
      throw new Error('Server returned invalid response. Product may not have been added to cart.');
    }
    
    // Check for API errors in the JSON response
    if (result.status && result.status !== 'success') {
      throw new Error(result.description || result.message || 'Failed to add product to cart');
    }
    
    // Use the response (has updated cart data)
    const cartData = result;
    
    // Trigger cart update event - same as product-form does
    if (typeof window.publish !== 'undefined' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
      await window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form',
        productVariantId: variantId,
        cartData: cartData
      });
      
      // If we have a cart element and sections were rendered, update the UI
      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(cartData);
      }
    } else {
      // Fallback: update cart UI manually
      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(cartData);
      } else {
        // Last resort: redirect to cart
        window.location.href = window.routes?.cart_url || '/cart';
      }
    }
  }

  updateMainPrice(option) {
    const price = parseInt(option.dataset.price);
    
    // Find the main price element
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return;
    
    const sectionId = productInfo.dataset.section;
    const priceElement = document.querySelector(`#price-${sectionId}`);
    if (!priceElement) return;
    
    // Get all price elements
    const priceItems = priceElement.querySelectorAll('.price-item, .price-item--regular, .price-item--sale');
    
    // Update regular price
    priceItems.forEach(item => {
      if (!item.closest('s') && !item.closest('.variant-item__old-price')) {
        // Format price using Shopify's money format
        const formattedPrice = this.formatMoney(price);
        item.textContent = formattedPrice;
      }
    });

    // Keep product pricing discount-free (no strikethrough / compare-at)
    const injectedCompare = priceElement.querySelector('.variant-item__old-price');
    if (injectedCompare) {
      injectedCompare.style.display = 'none';
    }
    const themeCompare = priceElement.querySelector('s.price-item--regular');
    if (themeCompare) {
      themeCompare.style.display = 'none';
    }
    priceElement.classList?.remove?.('price--on-sale');
    const priceRoot = priceElement.querySelector('.price');
    if (priceRoot) priceRoot.classList.remove('price--on-sale');
    const savings = priceElement.querySelector('.price__savings');
    if (savings) savings.style.display = 'none';
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    const amount = cents / 100;
    
    // Use Intl.NumberFormat for better currency formatting
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }
}

// Initialize when DOM is ready
function initProductSelector() {
  if (document.querySelector('[data-product-selector]')) {
    new ProductSelectorBundle();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductSelector);
} else {
  initProductSelector();
}

